import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label, Switch } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { EventType } from "@/types";

const blank = { slug: "", name_ar: "", name_en: "", active: true };
const toSlug = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30);

const Toggle = ({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (v: boolean) => void }) => (
  <label className="flex items-center justify-between gap-3 rounded-2xl border px-4 py-2.5 text-sm font-semibold">
    {label}<Switch checked={checked} onCheckedChange={onChange} data-testid={id} />
  </label>
);

export const EventTypeDialog = ({ open, type, onClose, onSaved }: { open: boolean; type: EventType | null; onClose: () => void; onSaved: () => void }) => {
  const { t } = usePrefs();
  const [f, setF] = useState(blank);
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setF(type ? { ...blank, ...type } : blank); setSlugTouched(false); }
  }, [open, type]);
  const set = (k: keyof typeof blank, v: any) => setF((p) => ({ ...p, [k]: v, ...(k === "name_en" && !type && !slugTouched ? { slug: toSlug(v) } : {}) }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { slug, ...rest } = f;
      if (type) await api.patch(`/admin/event-types/${type.slug}`, rest);
      else await api.post("/admin/event-types", f);
      toast.success(t(type ? "et.updated" : "et.created"));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  const cls = "h-11 rounded-2xl";
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent data-testid="event-type-dialog" className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t(type ? "et.edit" : "ae.newType")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>{t("et.nameAr")}</Label><Input required minLength={2} maxLength={40} value={f.name_ar} onChange={(e: any) => set("name_ar", e.target.value)} data-testid="event-type-name-ar" className={cls} /></div>
            <div className="space-y-1.5"><Label>{t("et.nameEn")}</Label><Input required minLength={2} maxLength={40} dir="ltr" value={f.name_en} onChange={(e: any) => set("name_en", e.target.value)} data-testid="event-type-name-en" className={cls} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("ae.slug")}</Label>
            <Input required dir="ltr" pattern="[a-z_]{2,30}" disabled={!!type} value={f.slug} onChange={(e: any) => { setSlugTouched(true); set("slug", e.target.value.toLowerCase()); }} data-testid="event-type-slug" className={cls} />
            <p className="text-xs text-muted-foreground">{t(type ? "et.slugLocked" : "et.slugHint")}</p>
          </div>
          <div className="grid gap-2">
            <Toggle id="event-type-active" label={t("ae.active")} checked={f.active} onChange={(v) => set("active", v)} />
          </div>
          {type && <p className="text-xs text-muted-foreground">{t("et.editNote")}</p>}
          <button disabled={busy} data-testid="event-type-save" className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50">{busy ? t("common.loading") : t("common.save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
